import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router';
import { useGetUserExternalDevicesQuery, useGetPreprocessedFilesInfoQuery, useGetUsersQuery, useGetUserAnnotationsQuery } from 'features/api/apiSlice';

// material-ui
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { ReconciliationOutlined, LineChartOutlined, CommentOutlined } from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EmptyUserCard from 'components/cards/skeleton/EmptyUserCard';
import { selectCurrentUser } from 'features/auth/authSlice';
import { useAppSelector } from 'app/hooks';
import { SessionChart } from './tabs/SessionChart';
import { SessionsTable } from './tabs/SessionsTable';
import AnnotationsTab from '../tabs/AnnotationsTab';
import { DeviceBrandId, DeviceBrandName } from 'models/ISensor';
import moment from 'moment';
  
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// ==============================|| CUSTOMER - CARD PREVIEW ||============================== //
  
export default function PatientFileDialog () {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);
  const { userid } = useParams();
  
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: null })

  const { data: annotations, isLoading: isLoadingAnnotations, isFetching: isFetchingAnnotations } = useGetUserAnnotationsQuery({
      userId: userid!!,
      from: null,
      to: moment.utc().endOf("day").toDate().toISOString()
    })

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetUserExternalDevicesQuery(userid!!)
  const syknoDevice = externalDevices?.find(d => d.assignedUserId === userid && d.brandId === DeviceBrandId.Sykno);

  const { data: preprocessedFilesInfo, isLoading: isLoadingPreprocessedFilesInfo, isFetching: isFetchingPreprocessedFilesInfo } = useGetPreprocessedFilesInfoQuery(
    { ContainerName: DeviceBrandName.Sykno.toLowerCase(), DeviceId: syknoDevice?.id ?? '' },
    { skip: !syknoDevice }
  );

  if (isLoadingUsers || isLoadingExternalDevices || isLoadingPreprocessedFilesInfo) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('loading', 'Loading...')} />
      </Grid>
    )
  } else if (isFetchingUsers || isFetchingExternalDevices || isFetchingPreprocessedFilesInfo) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('fetching', 'Fetching...')} />
      </Grid>
    )
  } else {
    const patient = users!!.find(x => x.id == userid)

    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto" aria-label="account profile tab">
            <Tab label={t('sessions', 'Sessions')} icon={<ReconciliationOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(0)}/>
            <Tab label={t('annotations', 'Annotations')} icon={<CommentOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(1)}/>
            <Tab label={t('charts', 'Charts')} icon={<LineChartOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(2)}/>
          </Tabs>
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <CustomTabPanel value={value} index={0}>
            <SessionsTable preprocessedFilesInfo={preprocessedFilesInfo ?? []} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <AnnotationsTab annotations={annotations!!} users={users!!.concat(authUser!!)} isLoading={isLoadingAnnotations} isFetching={isFetchingAnnotations} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <SessionChart patientId={patient!!.id!!} users={users!!.concat(authUser!!)} preprocessedFilesInfo={preprocessedFilesInfo ?? []}/>
          </CustomTabPanel>
          <Box sx={{ mt: 2.5 }}>
            <Outlet />
          </Box>
        </Box>
      </>
    )
  }
}
  